import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check, Users, Shield, Clock, ArrowRight, Menu, X } from 'lucide-react';

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const features = [
    {
      icon: '📝',
      title: 'Smart Form Builder',
      description:
        'Create professional liability waivers and forms in minutes with our intuitive drag-and-drop builder.',
    },
    {
      icon: '📱',
      title: 'Works Offline',
      description:
        'Forms work perfectly even without internet connection. Sync automatically when back online.',
    },
    {
      icon: '🔒',
      title: 'Secure Digital Signatures',
      description:
        'Legally binding digital signatures with advanced security features and audit trails.',
    },
    {
      icon: '☁️',
      title: 'Cloud Storage',
      description:
        'All documents are securely stored in the cloud with automatic backups and easy retrieval.',
    },
    {
      icon: '📊',
      title: 'Analytics Dashboard',
      description:
        'Track form submissions, completion rates, and rider data with powerful analytics tools.',
    },
    {
      icon: '🔄',
      title: 'Automated Workflows',
      description:
        'Set up automatic email notifications and follow-ups to streamline your operations.',
    },
  ];

  const benefits = [
    {
      title: 'Save Time',
      description:
        'Eliminate paper waivers and manual data entry. Digital forms streamline your check-in process.',
      icon: '⏰',
    },
    {
      title: 'Reduce Risk',
      description:
        'Ensure every rider signs a proper liability waiver with secure digital signatures.',
      icon: '🛡️',
    },
    {
      title: 'Stay Organized',
      description:
        'All documents stored safely in the cloud with easy search and retrieval when needed.',
      icon: '📁',
    },
  ];

  const pricingPlans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: '$20',
      period: '/month',
      description: 'Perfect for getting started',
      savings: null,
    },
    {
      id: 'yearly',
      name: 'Annual',
      price: '$200',
      period: '/year',
      description: 'Best value - save $40',
      savings: 'Save 17%',
    },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleStartTrial = () => {
    navigate('/request-access');
  };

  const handleContact = () => {
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-primary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src="/riderelease-logo.png" alt="RideRelease" className="h-10 w-10" />
              <span className="text-xl font-bold text-primary-800">RideRelease</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-secondary-700 hover:text-primary-700 transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-secondary-700 hover:text-primary-700 transition-colors"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-secondary-700 hover:text-primary-700 transition-colors"
              >
                Benefits
              </button>
              <button
                onClick={handleContact}
                className="text-secondary-700 hover:text-primary-700 transition-colors"
              >
                Contact
              </button>
              <button onClick={handleSignIn} className="btn-secondary">
                Sign In
              </button>
              <button onClick={handleStartTrial} className="btn-primary">
                Request Access
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-secondary-700 hover:text-primary-700"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden border-t border-primary-200 bg-white/95">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <button
                  onClick={() => scrollToSection('features')}
                  className="block w-full text-left px-3 py-2 text-secondary-700 hover:text-primary-700"
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="block w-full text-left px-3 py-2 text-secondary-700 hover:text-primary-700"
                >
                  Pricing
                </button>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="block w-full text-left px-3 py-2 text-secondary-700 hover:text-primary-700"
                >
                  Benefits
                </button>
                <button
                  onClick={handleContact}
                  className="block w-full text-left px-3 py-2 text-secondary-700 hover:text-primary-700"
                >
                  Contact
                </button>
                <div className="pt-2 space-y-2">
                  <button onClick={handleSignIn} className="w-full btn-secondary">
                    Sign In
                  </button>
                  <button onClick={handleStartTrial} className="w-full btn-primary">
                    Request Access
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-secondary-900 leading-tight">
                Digital Waivers
                <span className="block text-primary-700">Made Simple</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto lg:mx-0">
                Streamline your equestrian business with professional liability waivers, digital
                signatures, and automated form management.
              </p>

              {/* Built in Plains VA - Prominent Display */}
              <div className="mt-4 flex items-center justify-center lg:justify-start">
                <div className="bg-primary-100 px-4 py-2 rounded-full border border-primary-300">
                  <span className="text-primary-800 font-semibold text-sm">
                    🏇 Built in the Plains, VA by equestrians
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="mt-8 flex justify-center lg:justify-start">
                <button
                  onClick={handleStartTrial}
                  className="btn-primary text-lg px-8 py-4 flex items-center justify-center group"
                >
                  Request Access
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-secondary-600">
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-primary-600" />
                  <span>Bank-level security</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-primary-600" />
                  <span>Works offline</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-primary-600" />
                  <span>Setup in 5 minutes</span>
                </div>
              </div>
            </div>

            {/* Right Column - Hero Image/Visual */}
            <div className="mt-12 lg:mt-0 relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="bg-primary-50 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <img src="/riderelease-logo.png" alt="RideRelease" className="h-8 w-8" />
                    <span className="font-semibold text-primary-800">Liability Waiver</span>
                  </div>
                  <div className="space-y-3">
                    <div className="h-3 bg-secondary-200 rounded w-3/4"></div>
                    <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                    <div className="h-3 bg-secondary-200 rounded w-5/6"></div>
                    <div className="mt-6 h-12 bg-primary-600 rounded-lg flex items-center justify-center text-white font-medium">
                      ✓ Digitally Signed
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-accent-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                🐎 Horse-friendly
              </div>
              <div className="absolute -bottom-6 -left-6 bg-primary-700 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                📱 Mobile-ready
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              Everything you need to run your stable
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              From liability waivers to rider management, RideRelease provides all the tools you
              need to streamline your equestrian business operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card p-6 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">{feature.title}</h3>
                <p className="text-secondary-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Choose the plan that works best for your stable. No hidden fees, cancel anytime.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`card p-8 text-center bg-white relative ${plan.id === 'yearly' ? 'ring-2 ring-primary-600 ring-opacity-50' : ''}`}
              >
                {plan.savings && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                      {plan.savings}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">{plan.name}</h3>
                  <div className="text-5xl font-bold text-primary-700 mb-2">
                    {plan.price}
                    <span className="text-2xl text-secondary-600 font-normal">{plan.period}</span>
                  </div>
                  <p className="text-secondary-600">{plan.description}</p>
                </div>

                <div className="space-y-3 mb-8 text-left">
                  {[
                    'Unlimited liability waivers',
                    'Digital signature collection',
                    'Cloud storage & backups',
                    'Mobile-responsive forms',
                    'Analytics dashboard',
                    'Email notifications',
                    'Customer support',
                    'SSL security',
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <span className="text-secondary-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleStartTrial}
                  className={`w-full text-lg py-4 mb-4 ${plan.id === 'yearly' ? 'btn-primary' : 'btn-secondary'}`}
                >
                  Request Access
                </button>
                <p className="text-sm text-secondary-500">
                  Reach out to learn more about our plans
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              Why choose RideRelease?
            </h2>
            <p className="text-lg text-secondary-600">
              Built specifically for equestrian businesses to solve real problems
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="card p-8 text-center">
                <div className="text-5xl mb-6">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">{benefit.title}</h3>
                <p className="text-secondary-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-700 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to simplify your waiver process?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Start streamlining your waiver process today with RideRelease
          </p>
          <div className="flex justify-center">
            <button
              onClick={handleStartTrial}
              className="bg-white text-primary-700 font-semibold px-8 py-4 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center group"
            >
              Request Access
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="text-primary-200 text-sm mt-6">
            Or reach out directly:{' '}
            <a href="mailto:Sebastian@riderelease.pro" className="text-white underline">
              Sebastian@riderelease.pro
            </a>{' '}
            |{' '}
            <a href="tel:+15404223061" className="text-white underline">
              540-422-3061
            </a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src="/riderelease-logo.png" alt="RideRelease" className="h-10 w-10" />
                <span className="text-xl font-bold">RideRelease</span>
              </div>
              <p className="text-secondary-300 mb-6 max-w-md">
                Professional liability waiver and form management software designed specifically for
                the equestrian industry.
              </p>
              <div className="bg-primary-700 px-4 py-3 rounded-lg inline-block">
                <p className="text-white font-semibold flex items-center">
                  <span className="mr-2">🏇</span>
                  Built in the Plains, VA by equestrians
                </p>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-secondary-300">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    API
                  </a>
                </li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-secondary-300">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Request Access
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-secondary-700 mt-12 pt-8 text-center text-secondary-400">
            <p>&copy; 2024 RideRelease. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
